import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  BarChart3, Wallet, Package, Printer, AlertOctagon,
  AlertTriangle, Clock, History, TrendingDown, TrendingUp,
  ChevronDown, ChevronUp, Users, ArrowDownToLine, ArrowUpFromLine,
  GitCompare, ArrowUp, ArrowDown, Minus,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, Cell, ReferenceLine,
} from "recharts";
import api from "../lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input, Label, Select } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { InlineRowsSkeleton } from "../components/ui/Skeleton";
import PrintPreview from "../components/ui/PrintPreview";
import {
  RevenueReportPrint,
  DebtReportPrint,
  InventoryReportPrint,
} from "../components/print/ReportTemplates";
import { useI18n } from "../contexts/I18nContext";
import { formatVND, formatDate, formatDateTime } from "../lib/utils";
import { cn } from "../lib/utils";

// ─── Helper ──────────────────────────────────────────────────────────────────
function fmtLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}

const today = () => fmtLocal(new Date());
const monthAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return fmtLocal(d);
};

// ─── Component biểu đồ Phân tích doanh thu ───────────────────────────────────
function GrowthChart() {
  const thisYear  = new Date().getFullYear();
  const thisMonth = new Date().getMonth(); // 0-based
  const thisDay   = new Date();

  // ── Preset modes ────────────────────────────────────────────────────────────
  const PRESETS = [
    { id: "today_yesterday", label: "Hôm nay / Hôm qua" },
    { id: "this_last_week",  label: "Tuần này / Tuần trước" },
    { id: "this_last_month", label: "Tháng này / Tháng trước" },
    { id: "this_last_qtr",   label: "Quý này / Quý trước" },
    { id: "12months",        label: "12 tháng theo năm" },
    { id: "custom_days",     label: "Ngày / Ngày" },
  ];

  const [preset,   setPreset]   = useState("this_last_month");
  const [yearA,    setYearA]    = useState(thisYear);      // năm chính (12months)
  const [yearB,    setYearB]    = useState(thisYear - 1);  // năm so sánh (12months)
  const [customA,  setCustomA]  = useState({ from: fmtLocal(new Date(thisYear, thisMonth, 1)), to: today() });
  const [dayA, setDayA] = useState(today());
  const [dayB, setDayB] = useState(fmtLocal(new Date(new Date().setDate(new Date().getDate() - 1))));
  const [customB,  setCustomB]  = useState({ from: fmtLocal(new Date(thisYear, thisMonth - 1, 1)), to: fmtLocal(new Date(thisYear, thisMonth, 0)) });

  const [dataA, setDataA] = useState([]);
  const [dataB, setDataB] = useState([]);
  const [loading, setLoading] = useState(false);
  const [labelA, setLabelA] = useState("");
  const [labelB, setLabelB] = useState("");

  // Tính khoảng ngày từ preset
  const getRange = useCallback((p, yA, yB, cA, cB) => {
    const n = new Date();
    const ymd = (d) => fmtLocal(d);
    const startOfWeek = (d) => { const r = new Date(d); r.setDate(d.getDate() - (d.getDay() || 7) + 1); r.setHours(0,0,0,0); return r; };
    const endOfWeek   = (d) => { const r = startOfWeek(d); r.setDate(r.getDate() + 6); return r; };

    if (p === "today_yesterday") {
      const tod = new Date(n); tod.setHours(0,0,0,0);
      const yes = new Date(tod); yes.setDate(tod.getDate() - 1);
      return {
        a: { from: ymd(tod), to: ymd(tod),       label: "Hôm nay" },
        b: { from: ymd(yes), to: ymd(yes),        label: "Hôm qua" },
        period: "daily",
      };
    }
    if (p === "this_last_week") {
      const sw = startOfWeek(n); const ew = endOfWeek(n);
      const sw2 = new Date(sw); sw2.setDate(sw.getDate() - 7);
      const ew2 = new Date(ew); ew2.setDate(ew.getDate() - 7);
      return {
        a: { from: ymd(sw),  to: ymd(ew),  label: "Tuần này" },
        b: { from: ymd(sw2), to: ymd(ew2), label: "Tuần trước" },
        period: "daily",
      };
    }
    if (p === "this_last_month") {
      const m1s = new Date(n.getFullYear(), n.getMonth(), 1);
      const m1e = new Date(n.getFullYear(), n.getMonth() + 1, 0);
      const m2s = new Date(n.getFullYear(), n.getMonth() - 1, 1);
      const m2e = new Date(n.getFullYear(), n.getMonth(), 0);
      return {
        a: { from: ymd(m1s), to: ymd(m1e), label: `T${n.getMonth()+1}/${n.getFullYear()}` },
        b: { from: ymd(m2s), to: ymd(m2e), label: `T${n.getMonth()}/${n.getFullYear()}` },
        period: "daily",
      };
    }
    if (p === "this_last_qtr") {
      const q  = Math.floor(n.getMonth() / 3);
      const qs  = new Date(n.getFullYear(), q * 3, 1);
      const qe  = new Date(n.getFullYear(), q * 3 + 3, 0);
      const qs2 = new Date(n.getFullYear(), (q - 1) * 3, 1);
      const qe2 = new Date(n.getFullYear(), q * 3, 0);
      return {
        a: { from: ymd(qs),  to: ymd(qe),  label: `Q${q+1}/${n.getFullYear()}` },
        b: { from: ymd(qs2), to: ymd(qe2), label: `Q${q}/${n.getFullYear()}` },
        period: "daily",
      };
    }
    if (p === "12months") {
      return {
        a: { from: `${yA}-01-01`, to: `${yA}-12-31`, label: `Năm ${yA}` },
        b: { from: `${yB}-01-01`, to: `${yB}-12-31`, label: `Năm ${yB}` },
        period: "monthly",
      };
    }
    if (p === "custom_days") {
      return {
        a: { from: cA.dA, to: cA.dA, label: cA.dA },
        b: { from: cA.dB, to: cA.dB, label: cA.dB },
        period: "daily",
      };
    }
    // custom
    return {
      a: { from: cA.from, to: cA.to, label: `${cA.from} → ${cA.to}` },
      b: { from: cB.from, to: cB.to, label: `${cB.from} → ${cB.to}` },
      period: "daily",
    };
  }, []);

  const load = useCallback(async () => {
    const effectiveCustomA = preset === "custom_days" ? { ...customA, dA: dayA, dB: dayB } : customA;
    const range = getRange(preset, yearA, yearB, effectiveCustomA, customB);
    setLabelA(range.a.label);
    setLabelB(range.b.label);
    setLoading(true);
    try {
      const apiParam = (r) =>
        preset === "12months"
          ? api.get("/reports/revenue", { params: { period: "monthly", date_from: r.from, date_to: r.to } })
          : api.get("/reports/revenue", { params: { date_from: r.from, date_to: r.to } });
      const [r1, r2] = await Promise.all([apiParam(range.a), apiParam(range.b)]);
      setDataA(r1.data.data || []);
      setDataB(r2.data.data || []);
    } catch {
      setDataA([]); setDataB([]);
    } finally {
      setLoading(false);
    }
  }, [preset, yearA, yearB, customA, customB, dayA, dayB, getRange]);

  useEffect(() => { load(); }, [load]);

  // Ghép dữ liệu 2 kỳ thành 1 mảng cột áp nhau
  // Với 12months: ghép theo tháng (01→12), label ngắn T1..T12
  const chartData = useMemo(() => {
    if (preset === "12months") {
      return Array.from({ length: 12 }, (_, i) => {
        const mLabel = `T${i+1}`;
        const a = dataA.find(d => {
          const m = parseInt(d.label?.split("/")[0], 10);
          return m === i + 1;
        });
        const b = dataB.find(d => {
          const m = parseInt(d.label?.split("/")[0], 10);
          return m === i + 1;
        });
        return {
          label: mLabel,
          a: a?.revenue ?? 0,
          b: b?.revenue ?? 0,
          a_orders: a?.orders ?? 0,
          b_orders: b?.orders ?? 0,
        };
      });
    }
    const len = Math.max(dataA.length, dataB.length);
    return Array.from({ length: len }, (_, i) => ({
      label:    dataA[i]?.label ?? `${i+1}`,
      a:        dataA[i]?.revenue ?? 0,
      b:        dataB[i]?.revenue ?? 0,
      a_orders: dataA[i]?.orders  ?? 0,
      b_orders: dataB[i]?.orders  ?? 0,
    }));
  }, [dataA, dataB, preset]);

  // KPI tổng
  const totA = dataA.reduce((s, d) => s + d.revenue, 0);
  const totB = dataB.reduce((s, d) => s + d.revenue, 0);
  const pct  = totB > 0 ? ((totA - totB) / totB * 100) : totA > 0 ? 100 : 0;
  const ordA = dataA.reduce((s, d) => s + (d.orders || 0), 0);
  const ordB = dataB.reduce((s, d) => s + (d.orders || 0), 0);
  const ordPct = ordB > 0 ? ((ordA - ordB) / ordB * 100) : ordA > 0 ? 100 : 0;
  const avgA = ordA > 0 ? totA / ordA : 0;
  const avgB = ordB > 0 ? totB / ordB : 0;
  const avgPct = avgB > 0 ? ((avgA - avgB) / avgB * 100) : avgA > 0 ? 100 : 0;

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const a = payload.find(p => p.dataKey === "a")?.value ?? 0;
    const b = payload.find(p => p.dataKey === "b")?.value ?? 0;
    const dp = b > 0 ? ((a - b) / b * 100) : a > 0 ? 100 : 0;
    const up = dp > 0;
    return (
      <div className="bg-white border border-border rounded-xl shadow-xl p-3 text-xs min-w-[180px]">
        <div className="font-bold text-ink mb-2 border-b border-border pb-1">{label}</div>
        <div className="space-y-1.5">
          <div className="flex justify-between gap-6">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#2D4A22] inline-block"/>{labelA}</span>
            <span className="font-mono font-bold text-[#2D4A22]">{a.toLocaleString("vi-VN")}₫</span>
          </div>
          <div className="flex justify-between gap-6">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#D4C9B0] inline-block"/>{labelB}</span>
            <span className="font-mono text-ink-muted">{b.toLocaleString("vi-VN")}₫</span>
          </div>
        </div>
        <div className={`mt-2 pt-2 border-t border-border flex justify-between font-bold ${up ? "text-green-600" : dp < 0 ? "text-red-600" : "text-ink-muted"}`}>
          <span>{up ? "▲ Tăng" : dp < 0 ? "▼ Giảm" : "—"}</span>
          <span>{dp === 0 ? "±0%" : `${dp > 0 ? "+" : ""}${dp.toFixed(1)}%`}</span>
        </div>
      </div>
    );
  };

  const fmtV = (v) => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : `${v}`;
  const fmtFull = (v) => v.toLocaleString("vi-VN") + "₫";

  const PctBadge = ({ val }) => {
    const up = val > 0.05; const dn = val < -0.05;
    return (
      <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full ${up ? "bg-green-100 text-green-700" : dn ? "bg-red-100 text-red-700" : "bg-cream text-ink-muted"}`}>
        {up ? <ArrowUp size={10}/> : dn ? <ArrowDown size={10}/> : <Minus size={10}/>}
        {Math.abs(val).toFixed(1)}%
      </span>
    );
  };

  return (
    <div className="space-y-4">
      {/* ── Bảng điều khiển preset ── */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-ink-muted uppercase tracking-wider shrink-0">So sánh:</span>
            {PRESETS.map(p => (
              <button key={p.id} onClick={() => setPreset(p.id)}
                className={`text-sm px-4 py-2 rounded-xl border-2 font-medium transition-all ${
                  preset === p.id
                    ? "border-bamboo bg-bamboo text-white shadow-sm"
                    : "border-border text-ink-muted hover:border-bamboo/50 hover:text-bamboo hover:bg-bamboo/5"
                }`}>
                {p.label}
              </button>
            ))}
          </div>

          {/* 12months: chọn 2 năm */}
          {preset === "12months" && (
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[#2D4A22]" />
                <span className="text-xs font-semibold text-ink-muted">Năm chính:</span>
                <select value={yearA} onChange={e => setYearA(Number(e.target.value))}
                  className="h-8 px-2 text-sm border border-border rounded-lg bg-white font-semibold text-bamboo">
                  {Array.from({length: 6}, (_, i) => thisYear - i).map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <span className="text-ink-muted text-sm font-bold">vs</span>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[#D4C9B0]" />
                <span className="text-xs font-semibold text-ink-muted">Năm so sánh:</span>
                <select value={yearB} onChange={e => setYearB(Number(e.target.value))}
                  className="h-8 px-2 text-sm border border-border rounded-lg bg-white font-semibold text-amber-700">
                  {Array.from({length: 6}, (_, i) => thisYear - i).map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
          )}

          {/* custom_days: chọn 2 ngày bất kỳ */}
          {preset === "custom_days" && (
            <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[#2D4A22]" />
                <span className="text-xs font-semibold text-ink-muted">Ngày chính:</span>
                <input type="date" value={dayA} max={today()}
                  onChange={e => setDayA(e.target.value)}
                  className="h-8 px-2 text-sm border border-border rounded-lg bg-white font-semibold text-bamboo" />
              </div>
              <span className="text-ink-muted text-sm font-bold">vs</span>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm bg-[#D4C9B0]" />
                <span className="text-xs font-semibold text-ink-muted">Ngày so sánh:</span>
                <input type="date" value={dayB} max={today()}
                  onChange={e => setDayB(e.target.value)}
                  className="h-8 px-2 text-sm border border-border rounded-lg bg-white font-semibold text-amber-700" />
              </div>
            </div>
          )}

          {/* Hiển thị khoảng ngày đang xem */}
          {preset !== "12months" && (
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border/60 flex-wrap text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-[#2D4A22] inline-block"/>
                <strong className="text-bamboo">{labelA}</strong>
              </span>
              <span className="text-ink-muted font-bold">vs</span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-sm bg-[#D4C9B0] inline-block"/>
                <strong className="text-ink-muted">{labelB}</strong>
              </span>
              {!loading && <PctBadge val={pct} />}
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent><div className="h-72 flex items-center justify-center text-ink-muted text-sm animate-pulse">Đang tải dữ liệu...</div></CardContent></Card>
      ) : (
        <>
          {/* ── KPI 3 ô ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { label: "Doanh thu", a: totA, b: totB, p: pct,    fmt: fmtFull },
              { label: "Số đơn",    a: ordA, b: ordB, p: ordPct, fmt: v => `${v} đơn` },
              { label: "TB / đơn",  a: avgA, b: avgB, p: avgPct, fmt: v => `${fmtV(v)}₫` },
            ].map(kpi => {
              const up = kpi.p > 0.05; const dn = kpi.p < -0.05;
              return (
                <div key={kpi.label} className={`rounded-2xl p-4 border-2 transition-all ${up ? "border-green-200 bg-gradient-to-br from-green-50 to-white" : dn ? "border-red-200 bg-gradient-to-br from-red-50 to-white" : "border-border bg-cream/20"}`}>
                  <div className="text-[10px] uppercase tracking-widest text-ink-muted font-bold mb-3">{kpi.label}</div>
                  <div className="flex items-end justify-between gap-2">
                    <div>
                      <div className="font-heading text-xl font-bold text-ink">{kpi.fmt(kpi.a)}</div>
                      <div className="text-xs text-ink-muted mt-1">
                        <span className="font-medium">{labelB}:</span> {kpi.fmt(kpi.b)}
                      </div>
                    </div>
                    <div className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl font-bold text-sm ${up ? "bg-green-100 text-green-700" : dn ? "bg-red-100 text-red-700" : "bg-cream text-ink-muted"}`}>
                      {up ? <ArrowUp size={16}/> : dn ? <ArrowDown size={16}/> : <Minus size={16}/>}
                      <span className="text-xs mt-0.5">{Math.abs(kpi.p).toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Biểu đồ cột áp nhau ── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <BarChart3 size={16} className="text-bamboo"/>
                {preset === "12months"
                  ? `12 tháng — Năm ${yearA} vs Năm ${yearB}`
                  : `Doanh thu theo từng điểm — ${labelA} vs ${labelB}`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: -5, bottom: 0 }}
                    barGap={1} barCategoryGap={preset === "12months" ? "20%" : "28%"}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAE5D9" vertical={false}/>
                    <XAxis dataKey="label" stroke="#71717A" fontSize={preset === "12months" ? 11 : 10}
                      tickLine={false} axisLine={false}/>
                    <YAxis stroke="#71717A" fontSize={10} tickLine={false} axisLine={false}
                      tickFormatter={v => v >= 1_000_000 ? `${(v/1_000_000).toFixed(1)}M` : v >= 1000 ? `${v/1000}k` : v}/>
                    <Tooltip content={<CustomTooltip />}/>
                    <Legend
                      formatter={val => (
                        <span className="text-xs font-semibold" style={{ color: val === "a" ? "#2D4A22" : "#9C8E7A" }}>
                          {val === "a" ? labelA : labelB}
                        </span>
                      )}
                    />
                    {/* B (kỳ trước) render trước để nằm sau */}
                    <Bar dataKey="b" name="b" fill="#D4C9B0" radius={[3,3,0,0]}/>
                    <Bar dataKey="a" name="a" radius={[3,3,0,0]}>
                      {chartData.map((row, i) => (
                        <Cell key={i} fill={row.a >= row.b ? "#2D4A22" : "#D95D39"}/>
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="text-[10px] text-ink-muted text-center mt-2 flex items-center justify-center gap-4">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#2D4A22] inline-block"/> {labelA} ≥ {labelB} (tăng hoặc bằng)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#D95D39] inline-block"/> {labelA} &lt; {labelB} (giảm)</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-[#D4C9B0] inline-block"/> {labelB}</span>
              </div>
            </CardContent>
          </Card>

          {/* ── Bảng chi tiết ── */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Chi tiết từng điểm</CardTitle>
            </CardHeader>
            <CardContent className="!p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-cream/70 sticky top-0">
                    <tr>
                      <th className="text-left py-2.5 px-4 font-semibold text-ink-muted">
                        {preset === "12months" ? "Tháng" : "Kỳ"}
                      </th>
                      <th className="text-right py-2.5 px-4 font-semibold text-bamboo">{labelA}</th>
                      <th className="text-right py-2.5 px-4 font-semibold text-ink-muted">{labelB}</th>
                      <th className="text-right py-2.5 px-4 font-semibold">Chênh lệch</th>
                      <th className="text-center py-2.5 px-4 font-semibold">Tỉ lệ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chartData.map((row, i) => {
                      const diff = row.a - row.b;
                      const rp   = row.b > 0 ? (diff / row.b * 100) : row.a > 0 ? 100 : 0;
                      const up   = rp > 0.05; const dn = rp < -0.05;
                      return (
                        <tr key={i} className={`border-t border-border/50 hover:bg-cream/20 ${up ? "hover:bg-green-50/30" : dn ? "hover:bg-red-50/30" : ""}`}>
                          <td className="py-2.5 px-4 font-semibold">{row.label}</td>
                          <td className="py-2.5 px-4 text-right">
                            <span className="font-mono font-bold text-bamboo">{fmtFull(row.a)}</span>
                            <span className="text-ink-muted ml-1 font-normal">({row.a_orders}đ)</span>
                          </td>
                          <td className="py-2.5 px-4 text-right">
                            <span className="font-mono text-ink-muted">{fmtFull(row.b)}</span>
                            <span className="text-ink-muted ml-1 font-normal">({row.b_orders}đ)</span>
                          </td>
                          <td className={`py-2.5 px-4 text-right font-mono font-bold ${up ? "text-green-600" : dn ? "text-red-600" : "text-ink-muted"}`}>
                            {diff > 0 ? "+" : ""}{fmtFull(diff)}
                          </td>
                          <td className="py-2.5 px-4 text-center"><PctBadge val={rp}/></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="border-t-2 border-border bg-cream/50 font-bold">
                    <tr>
                      <td className="py-3 px-4 font-bold">TỔNG</td>
                      <td className="py-3 px-4 text-right font-mono text-bamboo">
                        {fmtFull(totA)} <span className="font-normal text-ink-muted">({ordA}đ)</span>
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-ink-muted">
                        {fmtFull(totB)} <span className="font-normal">({ordB}đ)</span>
                      </td>
                      <td className={`py-3 px-4 text-right font-mono ${pct > 0 ? "text-green-600" : pct < 0 ? "text-red-600" : "text-ink-muted"}`}>
                        {(totA-totB) > 0 ? "+" : ""}{fmtFull(totA - totB)}
                      </td>
                      <td className="py-3 px-4 text-center"><PctBadge val={pct}/></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}



// ─── Sub-component: Hàng khách hàng có thể mở rộng (tab Công nợ) ─────────────
function DebtCustomerRow({ d, onRefresh }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("orders");

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const [ordRes, payRes] = await Promise.all([
        api.get(`/debts/customers/${d.customer_id}/orders`),
        api.get("/debts/payments", { params: { customer_id: d.customer_id, direction: "in" } }),
      ]);
      setOrders(ordRes.data);
      setPayments(payRes.data);
    } finally {
      setLoading(false);
    }
  }, [d.customer_id]);

  const toggle = () => {
    if (!expanded) loadDetail();
    setExpanded((v) => !v);
  };

  return (
    <>
      <tr
        className={cn(
          "border-t border-border cursor-pointer hover:bg-cream/30 transition-colors",
          d.overdue && "bg-red-50/40",
          d.due_soon && !d.overdue && "bg-amber-50/40"
        )}
        onClick={toggle}
        data-testid={`debt-row-${d.customer_id}`}
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            {expanded ? <ChevronUp size={14} className="text-ink-muted" /> : <ChevronDown size={14} className="text-ink-muted" />}
            <span className="font-medium">{d.customer_name}</span>
            {d.overdue && <Badge variant="cancelled">Quá hạn</Badge>}
            {d.due_soon && !d.overdue && <Badge variant="lowstock">Sắp đến hạn</Badge>}
          </div>
        </td>
        <td className="py-3 px-4 text-xs text-ink-muted">{d.phone}</td>
        <td className="py-3 px-4 text-right font-bold text-terracotta font-mono">
          {formatVND(d.total_debt)}
        </td>
        <td className="py-3 px-4 text-right">{d.order_count}</td>
        <td className="py-3 px-4 text-right text-xs text-ink-muted">
          {d.earliest_due ? formatDate(d.earliest_due) : "—"}
        </td>
        <td className="py-3 px-4 text-right text-xs text-ink-muted">
          {formatDate(d.last_order)}
        </td>
      </tr>

      {expanded && (
        <tr className="border-t border-border/50">
          <td colSpan={6} className="p-0">
            <div className="bg-cream/20 px-6 py-4">
              {/* Sub-tabs */}
              <div className="flex gap-2 mb-4 border-b border-border">
                {[
                  { id: "orders", label: "Đơn nợ", icon: AlertOctagon },
                  { id: "history", label: "Lịch sử thanh toán", icon: History },
                ].map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={(e) => { e.stopPropagation(); setActiveTab(id); }}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors",
                      activeTab === id ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink"
                    )}
                  >
                    <Icon size={12} /> {label}
                  </button>
                ))}
              </div>

              {loading ? (
                <InlineRowsSkeleton rows={3} />
              ) : activeTab === "orders" ? (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-ink-muted">
                    <tr>
                      <th className="text-left pb-2">Mã đơn</th>
                      <th className="text-left pb-2">Ngày đặt</th>
                      <th className="text-right pb-2">Tổng tiền</th>
                      <th className="text-right pb-2">Đã trả</th>
                      <th className="text-right pb-2">Còn nợ</th>
                      <th className="text-right pb-2">Hạn thanh toán</th>
                      <th className="text-center pb-2">Trạng thái</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-4 text-ink-muted text-xs">Không có đơn nợ</td></tr>
                    ) : orders.map((o) => (
                      <tr key={o.order_id} className="border-t border-border/50">
                        <td className="py-2 font-mono text-xs">{o.order_id}</td>
                        <td className="py-2 text-xs text-ink-muted">{formatDate(o.created_at)}</td>
                        <td className="py-2 text-right font-mono text-xs">{formatVND(o.total)}</td>
                        <td className="py-2 text-right font-mono text-xs text-green-700">{formatVND(o.paid_amount || 0)}</td>
                        <td className="py-2 text-right font-mono text-xs font-bold text-terracotta">{formatVND(o.remaining_amount || 0)}</td>
                        <td className="py-2 text-right text-xs">
                          {o.due_date ? (
                            <span className={cn(o.overdue ? "text-red-600 font-medium" : o.due_soon ? "text-amber-600" : "text-ink-muted")}>
                              {formatDate(o.due_date)}
                              {o.overdue && " (quá hạn)"}
                              {o.due_soon && !o.overdue && ` (còn ${o.days_to_due}n)`}
                            </span>
                          ) : "—"}
                        </td>
                        <td className="py-2 text-center">
                          {o.overdue ? <Badge variant="cancelled">Quá hạn</Badge>
                            : o.due_soon ? <Badge variant="lowstock">Sắp hạn</Badge>
                            : <Badge variant="processing">Chờ TT</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-ink-muted">
                    <tr>
                      <th className="text-left pb-2">Thời gian</th>
                      <th className="text-left pb-2">Ghi chú</th>
                      <th className="text-left pb-2">Phương thức</th>
                      <th className="text-right pb-2">Số tiền</th>
                      <th className="text-left pb-2">Người thực hiện</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 ? (
                      <tr><td colSpan={5} className="text-center py-4 text-ink-muted text-xs">Chưa có lịch sử thanh toán</td></tr>
                    ) : payments.map((p, i) => (
                      <tr key={i} className="border-t border-border/50">
                        <td className="py-2 text-xs text-ink-muted">{formatDateTime(p.created_at)}</td>
                        <td className="py-2 text-xs">{p.notes || "—"}</td>
                        <td className="py-2 text-xs capitalize">{p.method || p.payment_method || "—"}</td>
                        <td className="py-2 text-right font-mono text-xs font-bold text-green-700">+{formatVND(p.amount)}</td>
                        <td className="py-2 text-xs text-ink-muted">{p.created_by_name || p.created_by || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function Reports() {
  const { t } = useI18n();
  const [tab, setTab] = useState("revenue");

  // Revenue state
  const [period, setPeriod] = useState("daily");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [revenue, setRevenue] = useState(null);

  // Debt state
  const [debt, setDebt] = useState(null);
  const [debtLoading, setDebtLoading] = useState(false);

  // Inventory state
  const [inventory, setInventory] = useState(null);
  const [invTab, setInvTab] = useState("snapshot"); // "snapshot" | "xnt"
  const [xntKind, setXntKind] = useState("product");
  const [xntFrom, setXntFrom] = useState(monthAgo());
  const [xntTo, setXntTo] = useState(today());
  const [xnt, setXnt] = useState(null);
  const [xntLoading, setXntLoading] = useState(false);

  const [printOpen, setPrintOpen] = useState(false);

  // ── Load revenue ──
  const loadRevenue = useCallback(async () => {
    const params = {};
    if (dateFrom && dateTo) { params.date_from = dateFrom; params.date_to = dateTo; }
    else params.period = period;
    const { data } = await api.get("/reports/revenue", { params });
    setRevenue(data);
  }, [dateFrom, dateTo, period]);

  // ── Load debt (dùng /debts/customers đầy đủ hơn) ──
  const loadDebt = useCallback(async () => {
    setDebtLoading(true);
    try {
      const { data } = await api.get("/debts/customers");
      setDebt(data);
    } finally {
      setDebtLoading(false);
    }
  }, []);

  // ── Load inventory snapshot ──
  const loadInventory = useCallback(async () => {
    const { data } = await api.get("/reports/inventory");
    setInventory(data);
  }, []);

  // ── Load XNT (Xuất Nhập Tồn) ──
  const loadXnt = useCallback(async () => {
    setXntLoading(true);
    try {
      const { data } = await api.get("/inventory/xnt", {
        params: { kind: xntKind, date_from: xntFrom, date_to: xntTo },
      });
      setXnt(data);
    } finally {
      setXntLoading(false);
    }
  }, [xntKind, xntFrom, xntTo]);

  useEffect(() => {
    if (tab === "revenue") loadRevenue();
    if (tab === "debt" && !debt) loadDebt();
    if (tab === "inventory" && !inventory) loadInventory();
  }, [tab, period, dateFrom, dateTo]);

  useEffect(() => {
    if (tab === "inventory" && invTab === "xnt") loadXnt();
  }, [tab, invTab, xntKind, xntFrom, xntTo]);

  const resetDateRange = () => { setDateFrom(""); setDateTo(""); };

  // ── Print config ──
  const printConfig = (() => {
    if (tab === "revenue") return {
      title: "Báo cáo doanh thu",
      filename: `bao-cao-doanh-thu-${today()}.pdf`,
      content: <RevenueReportPrint data={revenue?.data || []} period={period} dateFrom={dateFrom} dateTo={dateTo} />,
    };
    if (tab === "debt") return {
      title: "Báo cáo công nợ",
      filename: `bao-cao-cong-no-${today()}.pdf`,
      content: <DebtReportPrint
        items={(debt?.items || []).map(d => ({
          customer_name: d.customer_name, phone: d.phone,
          debt: d.total_debt, orders: d.order_count, last_order: d.last_order,
        }))}
        total={debt?.total || 0}
      />,
    };
    return {
      title: "Báo cáo tồn kho",
      filename: `bao-cao-ton-kho-${today()}.pdf`,
      content: <InventoryReportPrint data={inventory || {}} />,
    };
  })();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{t("reports.title")}</h1>
          <p className="text-sm text-ink-secondary mt-1">
            {t("reports.revenue")}, {t("reports.debt")}, {t("reports.inventory")}
          </p>
        </div>
        <Button variant="outline" onClick={() => setPrintOpen(true)} data-testid="reports-print-button">
          <Printer size={14} /> In / PDF
        </Button>
      </div>

      {/* Main tabs */}
      <div className="flex gap-2 border-b border-border">
        {[
          { id: "revenue",  icon: BarChart3,   label: t("reports.revenue") },
          { id: "growth",   icon: GitCompare,  label: "Tăng trưởng" },
          { id: "debt",     icon: Wallet,      label: t("reports.debt") },
          { id: "inventory",icon: Package,     label: t("reports.inventory") },
        ].map(({ id, icon: Icon, label }) => {
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={cn(
                "flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px",
                active ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink"
              )}
              data-testid={`reports-tab-${id}`}
            >
              <Icon size={14} /> {label}
            </button>
          );
        })}
      </div>

      {/* ── TAB: DOANH THU ─────────────────────────────────────────────────── */}
      {tab === "revenue" && revenue && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle>{t("reports.revenue")}</CardTitle>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-end gap-2">
                  <div>
                    <Label className="!mb-0.5">Từ</Label>
                    <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 text-xs" data-testid="reports-date-from" />
                  </div>
                  <div>
                    <Label className="!mb-0.5">Đến</Label>
                    <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 text-xs" data-testid="reports-date-to" />
                  </div>
                  {(dateFrom || dateTo) && (
                    <Button type="button" size="sm" variant="ghost" onClick={resetDateRange} data-testid="reports-date-reset">Xóa</Button>
                  )}
                </div>
                <div className="flex gap-1">
                  {["daily", "weekly", "monthly", "yearly"].map((p) => (
                    <Button
                      key={p} size="sm"
                      variant={(!dateFrom && !dateTo && period === p) ? "primary" : "outline"}
                      onClick={() => { setPeriod(p); resetDateRange(); }}
                      data-testid={`reports-period-${p}`}
                    >
                      {t(`reports.${p}`)}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenue.data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE5D9" vertical={false} />
                  <XAxis dataKey="label" stroke="#71717A" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#71717A" fontSize={11} tickLine={false} axisLine={false}
                    tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : `${v / 1000}k`} />
                  <Tooltip contentStyle={{ background: "white", border: "1px solid #E4E4E7", borderRadius: 8, fontSize: 12 }}
                    formatter={(v) => formatVND(v)} />
                  <Bar dataKey="revenue" fill="#2D4A22" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── TAB: TĂNG TRƯỞNG ───────────────────────────────────────────────── */}
      {tab === "growth" && <GrowthChart />}

      {/* ── TAB: CÔNG NỢ ───────────────────────────────────────────────────── */}
      {tab === "debt" && (
        <>
          {debtLoading ? (
            <Card><CardContent><InlineRowsSkeleton rows={5} /></CardContent></Card>
          ) : debt ? (
            <>
              {/* Tổng quan công nợ */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink-muted mb-1">
                      <Users size={12} /> Tổng công nợ
                    </div>
                    <div className="font-heading text-xl font-bold text-terracotta" data-testid="reports-debt-total">
                      {formatVND(debt.total)}
                    </div>
                    <div className="text-xs text-ink-muted mt-0.5">{debt.items.length} khách hàng</div>
                  </CardContent>
                </Card>
                <Card className={debt.overdue_total > 0 ? "border-red-300 ring-1 ring-red-100" : ""}>
                  <CardContent>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink-muted mb-1">
                      <AlertOctagon size={12} /> Quá hạn
                    </div>
                    <div className="font-heading text-xl font-bold text-red-600">
                      {formatVND(debt.overdue_total)}
                    </div>
                    <div className="text-xs text-ink-muted mt-0.5">
                      {debt.items.filter(d => d.overdue).length} khách
                    </div>
                  </CardContent>
                </Card>
                <Card className={debt.due_soon_total > 0 ? "border-amber-300 ring-1 ring-amber-100" : ""}>
                  <CardContent>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-ink-muted mb-1">
                      <Clock size={12} /> Sắp đến hạn (3 ngày)
                    </div>
                    <div className="font-heading text-xl font-bold text-amber-600">
                      {formatVND(debt.due_soon_total)}
                    </div>
                    <div className="text-xs text-ink-muted mt-0.5">
                      {debt.items.filter(d => d.due_soon && !d.overdue).length} khách
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Bảng danh sách khách hàng */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t("reports.debt")}</CardTitle>
                    <Button size="sm" variant="outline" onClick={loadDebt}>Làm mới</Button>
                  </div>
                </CardHeader>
                <CardContent className="!p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                      <tr>
                        <th className="text-left py-3 px-4">{t("orders.customer")}</th>
                        <th className="text-left py-3 px-4">{t("common.phone")}</th>
                        <th className="text-right py-3 px-4">{t("reports.debtAmount")}</th>
                        <th className="text-right py-3 px-4">{t("customers.totalOrders")}</th>
                        <th className="text-right py-3 px-4">Hạn sớm nhất</th>
                        <th className="text-right py-3 px-4">{t("reports.lastOrder")}</th>
                      </tr>
                    </thead>
                    <tbody data-testid="reports-debt-table">
                      {debt.items.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-12 text-ink-muted">{t("common.empty")}</td></tr>
                      ) : debt.items.map((d) => (
                        <DebtCustomerRow key={d.customer_id} d={d} onRefresh={loadDebt} />
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          ) : null}
        </>
      )}

      {/* ── TAB: TỒN KHO ───────────────────────────────────────────────────── */}
      {tab === "inventory" && (
        <>
          {/* Sub-tabs tồn kho */}
          <div className="flex gap-2 border-b border-border/60">
            {[
              { id: "snapshot", label: "Tồn kho hiện tại", icon: Package },
              { id: "xnt", label: "Lịch sử Xuất Nhập Tồn", icon: History },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setInvTab(id)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px",
                  invTab === id ? "border-bamboo text-bamboo" : "border-transparent text-ink-muted hover:text-ink"
                )}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>

          {/* ── Sub-tab: Tồn kho hiện tại ── */}
          {invTab === "snapshot" && (
            <>
              {!inventory ? (
                <Card><CardContent><InlineRowsSkeleton rows={5} /></CardContent></Card>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card>
                      <CardContent>
                        <div className="text-xs uppercase tracking-wider text-ink-muted">{t("dashboard.totalProducts")}</div>
                        <div className="font-heading text-2xl font-bold mt-1">{inventory.total_products}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent>
                        <div className="text-xs uppercase tracking-wider text-ink-muted">{t("reports.totalValue")}</div>
                        <div className="font-heading text-xl font-bold mt-1">{formatVND(inventory.total_stock_value)}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent>
                        <div className="text-xs uppercase tracking-wider text-ink-muted">{t("products.lowStockOnly")}</div>
                        <div className="font-heading text-2xl font-bold mt-1 text-amber-600">{inventory.low_stock_count}</div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent>
                        <div className="text-xs uppercase tracking-wider text-ink-muted">{t("reports.outOfStock")}</div>
                        <div className="font-heading text-2xl font-bold mt-1 text-red-600">{inventory.out_of_stock_count}</div>
                      </CardContent>
                    </Card>
                    <Card className={inventory.negative_stock_count > 0 ? "border-red-300 ring-1 ring-red-200" : ""}>
                      <CardContent>
                        <div className="text-xs uppercase tracking-wider text-ink-muted">Âm hàng</div>
                        <div className="font-heading text-2xl font-bold mt-1 text-red-700">{inventory.negative_stock_count || 0}</div>
                      </CardContent>
                    </Card>
                  </div>

                  {inventory.negative_stock_count > 0 && (
                    <Card className="border-red-300 bg-red-50/30">
                      <CardHeader>
                        <div className="flex items-center gap-2">
                          <AlertOctagon size={18} className="text-red-700" />
                          <CardTitle className="text-red-800">Hàng âm — cần kiểm tra ngay</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent className="!p-0">
                        <table className="w-full text-sm">
                          <thead className="bg-red-50 text-xs uppercase text-red-700/70">
                            <tr>
                              <th className="text-left py-3 px-4">Tên hàng</th>
                              <th className="text-left py-3 px-4">Danh mục</th>
                              <th className="text-right py-3 px-4">Tồn</th>
                            </tr>
                          </thead>
                          <tbody data-testid="reports-negative-stock-table">
                            {inventory.negative_stock_products.map((p) => (
                              <tr key={p.product_id} className="border-t border-red-200" data-testid={`negative-row-${p.product_id}`}>
                                <td className="py-3 px-4 font-medium">{p.name}</td>
                                <td className="py-3 px-4 text-xs">{p.category}</td>
                                <td className="py-3 px-4 text-right font-mono font-bold text-red-700">{p.stock} {p.unit}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </CardContent>
                    </Card>
                  )}

                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle>{t("reports.inventory")}</CardTitle>
                        <Button size="sm" variant="outline" onClick={loadInventory}>Làm mới</Button>
                      </div>
                    </CardHeader>
                    <CardContent className="!p-0">
                      <table className="w-full text-sm">
                        <thead className="bg-cream/40 text-xs uppercase text-ink-muted">
                          <tr>
                            <th className="text-left py-3 px-4">{t("products.name")}</th>
                            <th className="text-left py-3 px-4">{t("products.category")}</th>
                            <th className="text-right py-3 px-4">{t("products.stock")}</th>
                            <th className="text-right py-3 px-4">{t("products.cost")}</th>
                            <th className="text-right py-3 px-4">{t("reports.totalValue")}</th>
                            <th className="text-center py-3 px-4">{t("common.status")}</th>
                          </tr>
                        </thead>
                        <tbody data-testid="reports-inventory-table">
                          {inventory.products.map((p) => {
                            const value = (p.stock || 0) * (p.cost || 0);
                            const negative = p.stock < 0;
                            const out = p.stock === 0;
                            const low = !out && !negative && p.stock <= p.low_stock_threshold;
                            return (
                              <tr key={p.product_id} className="border-t border-border" data-testid={`inventory-row-${p.product_id}`}>
                                <td className="py-3 px-4 font-medium">{p.name}</td>
                                <td className="py-3 px-4 text-xs">{p.category}</td>
                                <td className={cn("py-3 px-4 text-right", negative && "text-red-700 font-bold")}>
                                  {p.stock} {p.unit}
                                </td>
                                <td className="py-3 px-4 text-right font-mono text-xs">{formatVND(p.cost)}</td>
                                <td className="py-3 px-4 text-right font-mono">{formatVND(value)}</td>
                                <td className="py-3 px-4 text-center">
                                  {negative ? <Badge variant="cancelled">Âm</Badge>
                                    : out ? <Badge variant="cancelled">{t("reports.outOfStock")}</Badge>
                                    : low ? <Badge variant="lowstock">{t("products.lowStockOnly")}</Badge>
                                    : <Badge variant="delivered">OK</Badge>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}

          {/* ── Sub-tab: Lịch sử Xuất Nhập Tồn ── */}
          {invTab === "xnt" && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <CardTitle className="flex items-center gap-2">
                    <History size={16} /> Lịch sử Xuất Nhập Tồn
                  </CardTitle>
                  <div className="flex items-center gap-3 flex-wrap">
                    {/* Loại hàng */}
                    <div className="flex gap-1">
                      {[{ id: "product", label: "Thành phẩm" }, { id: "material", label: "Nguyên liệu" }].map(({ id, label }) => (
                        <Button
                          key={id} size="sm"
                          variant={xntKind === id ? "primary" : "outline"}
                          onClick={() => setXntKind(id)}
                        >
                          {label}
                        </Button>
                      ))}
                    </div>
                    {/* Khoảng thời gian */}
                    <div className="flex items-end gap-2">
                      <div>
                        <Label className="!mb-0.5">Từ</Label>
                        <Input type="date" value={xntFrom} onChange={(e) => setXntFrom(e.target.value)} className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="!mb-0.5">Đến</Label>
                        <Input type="date" value={xntTo} onChange={(e) => setXntTo(e.target.value)} className="h-8 text-xs" />
                      </div>
                      <Button size="sm" variant="outline" onClick={loadXnt}>Xem</Button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="!p-0">
                {xntLoading ? (
                  <div className="p-6"><InlineRowsSkeleton rows={6} /></div>
                ) : xnt ? (
                  <>
                    {/* Tổng kết */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border-b border-border bg-cream/20">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-ink-muted">Tồn đầu kỳ</div>
                        <div className="font-heading text-lg font-bold mt-0.5">{xnt.totals.total_opening.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-green-700">
                          <ArrowDownToLine size={11} /> Nhập kỳ
                        </div>
                        <div className="font-heading text-lg font-bold mt-0.5 text-green-700">
                          +{xnt.totals.total_in.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-1 text-xs uppercase tracking-wider text-terracotta">
                          <ArrowUpFromLine size={11} /> Xuất kỳ
                        </div>
                        <div className="font-heading text-lg font-bold mt-0.5 text-terracotta">
                          -{xnt.totals.total_out.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-ink-muted">Tồn cuối kỳ</div>
                        <div className="font-heading text-lg font-bold mt-0.5">{xnt.totals.total_ending.toLocaleString()}</div>
                      </div>
                    </div>

                    {/* Bảng chi tiết */}
                    <table className="w-full text-sm">
                      <thead className="bg-cream/40 text-xs uppercase text-ink-muted sticky top-0">
                        <tr>
                          <th className="text-left py-3 px-4">Tên hàng</th>
                          <th className="text-right py-3 px-4">Tồn đầu kỳ</th>
                          <th className="text-right py-3 px-4">
                            <span className="flex items-center justify-end gap-1 text-green-700">
                              <ArrowDownToLine size={10} /> Nhập
                            </span>
                          </th>
                          <th className="text-right py-3 px-4">
                            <span className="flex items-center justify-end gap-1 text-terracotta">
                              <ArrowUpFromLine size={10} /> Xuất
                            </span>
                          </th>
                          <th className="text-right py-3 px-4">Tồn cuối kỳ</th>

                        </tr>
                      </thead>
                      <tbody>
                        {xnt.items.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-12 text-ink-muted">
                              Không có dữ liệu trong khoảng thời gian này
                            </td>
                          </tr>
                        ) : xnt.items.map((row) => {
                          return (
                            <tr key={row.id} className="border-t border-border hover:bg-cream/20">
                              <td className="py-3 px-4 font-medium">
                                {row.name}
                                <span className="text-xs text-ink-muted ml-1">({row.unit})</span>
                              </td>
                              <td className="py-3 px-4 text-right font-mono text-xs">
                                {row.opening_stock.toLocaleString()}
                              </td>
                              <td className="py-3 px-4 text-right font-mono text-xs font-medium text-green-700">
                                {row.total_in > 0 ? `+${row.total_in.toLocaleString()}` : "—"}
                              </td>
                              <td className="py-3 px-4 text-right font-mono text-xs font-medium text-terracotta">
                                {row.total_out > 0 ? `-${row.total_out.toLocaleString()}` : "—"}
                              </td>
                              <td className="py-3 px-4 text-right font-mono text-sm font-semibold">
                                {row.ending_stock.toLocaleString()}
                              </td>

                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </>
                ) : (
                  <div className="text-center py-12 text-ink-muted text-sm">
                    Chọn khoảng thời gian và nhấn "Xem" để tải dữ liệu
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Print Preview */}
      <PrintPreview
        open={printOpen}
        onClose={() => setPrintOpen(false)}
        title={printConfig.title}
        printId="report-print-area"
        pdfFilename={printConfig.filename}
        pdfFormat="a4"
      >
        {printConfig.content}
      </PrintPreview>
    </div>
  );
}